import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Plus, Trash2, Users, BarChart3, Sparkles, Loader2, Globe, TrendingUp, Search, ArrowRight } from 'lucide-react'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from 'sonner'
import {
  useCompetitors,
  useAddCompetitor,
  useDeleteCompetitor,
  useCompetitorCompare,
  useCompetitorVisibility,
} from '../hooks/useCompetitors'
import { useDiscoverCompetitors, useKeywordGap, useImportKeywords, type DiscoveredCompetitor, type GapKeyword } from '../hooks/useLabs'
import { rankColor } from '@/client/lib/colors'

function rankBadge(position: number | null) {
  if (position == null) return <span className="text-muted-foreground text-xs">-</span>
  return <span className={`text-xs font-medium ${rankColor(position)}`}>{position}</span>
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K'
  return String(Math.round(n))
}

function DiscoverCompetitorsSection({ siteId, tier }: { siteId: string; tier: string }) {
  const [enabled, setEnabled] = useState(false)
  const { data, isLoading } = useDiscoverCompetitors(siteId, enabled)
  const addCompetitor = useAddCompetitor(siteId)
  const { data: existingData } = useCompetitors(siteId)

  if (tier !== 'managed') return null

  const existingDomains = new Set(
    (existingData?.competitors ?? []).map((c) => c.competitor_domain.toLowerCase())
  )

  const handleAdd = (comp: DiscoveredCompetitor) => {
    addCompetitor.mutate(
      { domain: comp.domain },
      {
        onSuccess: (r) => {
          if (r.count > 0) toast.success(`Added ${comp.domain}`)
          else toast.info('Already tracked')
        },
        onError: () => toast.error('Failed to add'),
      },
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Auto-Discover Competitors
          </CardTitle>
          {!enabled && (
            <Button size="sm" variant="outline" onClick={() => setEnabled(true)}>
              <Sparkles className="mr-1 h-3.5 w-3.5" />
              Discover
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!enabled && (
          <p className="text-sm text-muted-foreground">
            Find competitors automatically based on SERP keyword overlap. Uses DataForSEO Labs API.
          </p>
        )}

        {enabled && isLoading && (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        )}

        {enabled && !isLoading && data && (
          <>
            {data.competitors.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No competitors found. The site may not have enough ranked keywords for overlap analysis.
              </p>
            ) : (
              <div className="space-y-2">
                {data.competitors.map((comp) => {
                  const isTracked = existingDomains.has(comp.domain.toLowerCase())
                  return (
                    <div
                      key={comp.domain}
                      className={`flex items-center justify-between p-3 rounded-lg border ${isTracked ? 'opacity-50' : ''}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <span className="text-sm font-medium truncate">{comp.domain}</span>
                          {isTracked && <Badge variant="outline" className="text-[10px] px-1">Tracked</Badge>}
                        </div>
                        <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                          <span>{comp.intersections} shared keywords</span>
                          <span>Avg pos: {comp.avg_position.toFixed(1)}</span>
                          <span className="hidden sm:inline">
                            <TrendingUp className="inline h-3 w-3 mr-0.5" />
                            {formatNumber(comp.etv)} est. traffic
                          </span>
                        </div>
                      </div>
                      {!isTracked && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="ml-2 shrink-0"
                          onClick={() => handleAdd(comp)}
                          disabled={addCompetitor.isPending}
                        >
                          {addCompetitor.isPending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Plus className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function CompetitorList({ siteId }: { siteId: string }) {
  const { data, isLoading } = useCompetitors(siteId)
  const addCompetitor = useAddCompetitor(siteId)
  const deleteCompetitor = useDeleteCompetitor(siteId)
  const [newDomain, setNewDomain] = useState('')

  const handleAdd = () => {
    const domain = newDomain.trim()
    if (!domain) return
    addCompetitor.mutate(
      { domain },
      {
        onSuccess: (r) => {
          if (r.count > 0) toast.success(`Added ${r.added.join(', ')}`)
          else toast.info('Competitor already exists')
          setNewDomain('')
        },
        onError: () => toast.error('Failed to add competitor'),
      },
    )
  }

  if (isLoading) return <Skeleton className="h-32 w-full" />

  const competitors = data?.competitors ?? []

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4" />
          Competitors
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            placeholder="competitor.com"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
          <Button size="sm" onClick={handleAdd} disabled={addCompetitor.isPending}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add
          </Button>
        </div>

        {competitors.length === 0 && (
          <p className="text-sm text-muted-foreground py-2">
            Add competitor domains to compare rankings. Competitor positions are captured automatically during rank checks at no extra cost.
          </p>
        )}

        {competitors.map((comp) => (
          <div key={comp.id} className="flex items-center justify-between py-1.5 border-b last:border-0">
            <span className="text-sm">{comp.competitor_domain}</span>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-destructive"
              onClick={() =>
                deleteCompetitor.mutate(comp.id, {
                  onSuccess: () => toast.success('Competitor removed'),
                  onError: () => toast.error('Failed to remove'),
                })
              }
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function ComparisonTable({ siteId, domain }: { siteId: string; domain: string }) {
  const { data, isLoading } = useCompetitorCompare(siteId)

  if (isLoading) return <Skeleton className="h-64 w-full" />
  if (!data || data.comparison.length === 0) return null

  const comps = data.competitors

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Rank Comparison</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Keyword</th>
                <th className="text-center py-2 px-2 font-medium text-muted-foreground min-w-[60px]">{domain}</th>
                {comps.map((c) => (
                  <th key={c.id} className="text-center py-2 px-2 font-medium text-muted-foreground min-w-[60px]">
                    {c.domain.length > 15 ? c.domain.slice(0, 12) + '...' : c.domain}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.comparison.map((row) => (
                <tr key={row.keywordId} className="border-b last:border-0">
                  <td className="py-1.5 pr-4">
                    <span className="truncate block max-w-[200px]" title={row.keyword}>{row.keyword}</span>
                  </td>
                  <td className="text-center py-1.5 px-2">{rankBadge(row.ourPosition)}</td>
                  {comps.map((c) => (
                    <td key={c.id} className="text-center py-1.5 px-2">
                      {rankBadge(row.competitors[c.id] ?? null)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

function VisibilityChart({ siteId, domain }: { siteId: string; domain: string }) {
  const { data, isLoading } = useCompetitorVisibility(siteId)

  if (isLoading) return <Skeleton className="h-64 w-full" />
  if (!data || data.totalVisibility === 0) return null

  const chartData = [
    { name: domain.length > 15 ? domain.slice(0, 12) + '...' : domain, sov: Math.round(data.our.shareOfVoice * 10) / 10 },
    ...data.competitors.map((c) => ({
      name: c.domain.length > 15 ? c.domain.slice(0, 12) + '...' : c.domain,
      sov: Math.round(c.shareOfVoice * 10) / 10,
    })),
  ]

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Share of Voice
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {data.our.shareOfVoice.toFixed(1)}%
            </div>
            <div className="text-xs text-muted-foreground">{domain}</div>
          </div>
          {data.competitors.map((c) => (
            <div key={c.id} className="text-center">
              <div className="text-2xl font-bold text-muted-foreground">
                {c.shareOfVoice.toFixed(1)}%
              </div>
              <div className="text-xs text-muted-foreground">{c.domain}</div>
            </div>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
            <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              formatter={(value) => [`${value}%`, 'Share of Voice']}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar
              dataKey="sov"
              name="Share of Voice"
              fill="hsl(221, 83%, 53%)"
              radius={[0, 4, 4, 0]}
              maxBarSize={24}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

function IntentBadge({ intent }: { intent: string | null }) {
  if (!intent) return null
  const colors: Record<string, string> = {
    informational: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    navigational: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    commercial: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
    transactional: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  }
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${colors[intent] ?? 'bg-muted text-muted-foreground'}`}>
      {intent.slice(0, 3)}
    </span>
  )
}

function KeywordGapSection({ siteId, tier }: { siteId: string; tier: string }) {
  const [selectedCompetitor, setSelectedCompetitor] = useState('')
  const { data: competitorsData } = useCompetitors(siteId)
  const { data: gapData, isLoading } = useKeywordGap(siteId, selectedCompetitor)
  const importKeywords = useImportKeywords(siteId)

  if (tier !== 'managed') return null

  const competitors = competitorsData?.competitors ?? []
  const keywords = gapData?.keywords ?? []

  const handleImport = (kw: GapKeyword) => {
    importKeywords.mutate([kw.keyword], {
      onSuccess: (r) => {
        if (r.added > 0) toast.success(`Tracking "${kw.keyword}"`)
        else toast.info('Already tracked')
      },
      onError: () => toast.error('Failed to import'),
    })
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4" />
            Keyword Gap
          </CardTitle>
          {competitors.length > 0 && (
            <Select value={selectedCompetitor} onValueChange={setSelectedCompetitor}>
              <SelectTrigger className="w-[200px] h-8 text-xs">
                <SelectValue placeholder="Select competitor..." />
              </SelectTrigger>
              <SelectContent>
                {competitors.map((c) => (
                  <SelectItem key={c.id} value={c.competitor_domain}>
                    {c.competitor_domain}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {competitors.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Add competitors above to discover keyword gaps — keywords they rank for that you don't.
          </p>
        )}

        {competitors.length > 0 && !selectedCompetitor && (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Select a competitor to find keywords they rank for that you don't.
          </p>
        )}

        {isLoading && (
          <div className="space-y-3 py-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}

        {selectedCompetitor && !isLoading && keywords.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No keyword gaps found for this competitor.
          </p>
        )}

        {keywords.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Keyword</th>
                  <th className="text-center py-2 px-2 font-medium text-muted-foreground">Volume</th>
                  <th className="text-center py-2 px-2 font-medium text-muted-foreground">Intent</th>
                  <th className="text-center py-2 px-2 font-medium text-muted-foreground">Comp. Pos</th>
                  <th className="text-center py-2 px-2 font-medium text-muted-foreground">Your Pos</th>
                  <th className="text-center py-2 px-2 font-medium text-muted-foreground">CPC</th>
                  <th className="text-right py-2 font-medium text-muted-foreground">Track</th>
                </tr>
              </thead>
              <tbody>
                {keywords.map((kw) => (
                  <tr key={kw.keyword} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-1.5 pr-4">
                      <span className="truncate block max-w-[250px]" title={kw.keyword}>{kw.keyword}</span>
                    </td>
                    <td className="text-center py-1.5 px-2 text-xs">
                      {kw.search_volume != null ? formatNumber(kw.search_volume) : '-'}
                    </td>
                    <td className="text-center py-1.5 px-2">
                      <IntentBadge intent={kw.intent} />
                    </td>
                    <td className="text-center py-1.5 px-2">
                      {rankBadge(kw.competitor_position)}
                    </td>
                    <td className="text-center py-1.5 px-2">
                      {kw.our_position != null
                        ? rankBadge(kw.our_position)
                        : <span className="text-xs text-muted-foreground italic">Not Ranking</span>
                      }
                    </td>
                    <td className="text-center py-1.5 px-2 text-xs text-muted-foreground">
                      {kw.cpc != null ? `$${kw.cpc.toFixed(2)}` : '-'}
                    </td>
                    <td className="text-right py-1.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2"
                        onClick={() => handleImport(kw)}
                        disabled={importKeywords.isPending}
                        title="Import to rank tracker"
                      >
                        <ArrowRight className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function CompetitorsTab({ siteId, domain, tier }: { siteId: string; domain: string; tier: string }) {
  return (
    <div className="space-y-6">
      <DiscoverCompetitorsSection siteId={siteId} tier={tier} />
      <CompetitorList siteId={siteId} />
      <KeywordGapSection siteId={siteId} tier={tier} />
      <VisibilityChart siteId={siteId} domain={domain} />
      <ComparisonTable siteId={siteId} domain={domain} />
    </div>
  )
}
